import { LogOut, ChevronDown, Shield, Building2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Badge } from '@/components/ui/badge';
import { useOrgStore } from '@/stores/useOrgStore';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { usePermissions } from '@/hooks/usePermissions';
import Logo from '@/components/Logo';
import { SearchBar } from '@/components/search/SearchBar';
import { TopQuoteBar } from '@/components/TopQuoteBar';

const Topbar = () => {
  const { activeOrg, activeRole, memberships, switchOrg } = useOrgStore();
  const { permissionCount } = usePermissions();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      toast.error('Error signing out');
    } else {
      toast.success('Signed out successfully');
      navigate('/auth');
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/60">
      <div className="flex h-16 items-center px-6 gap-4">
        <Logo size={36} />
        
        <div className="flex-1 flex justify-center px-8">
          <TopQuoteBar />
        </div>
        
        <div className="flex items-center gap-4">
          <SearchBar />
          {activeOrg && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2">
                  <Building2 className="h-4 w-4" />
                  <span>{activeOrg.name}</span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="flex flex-col">
                    <span>{activeOrg.name}</span>
                    <span className="text-xs font-normal text-muted-foreground">
                      Role: {activeRole}
                    </span>
                    {permissionCount && (
                      <Badge variant="secondary" className="mt-2 gap-1">
                        <Shield className="h-3 w-3" />
                        {permissionCount.granted}/{permissionCount.total} permissions
                      </Badge>
                    )}
                  </div>
                </DropdownMenuLabel>
                {memberships.length > 1 && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuLabel className="text-xs text-muted-foreground">
                      Switch Organization
                    </DropdownMenuLabel>
                    {memberships.map((membership) => (
                      <DropdownMenuItem
                        key={membership.org_id}
                        onClick={() => switchOrg(membership.org_id)}
                        className={
                          membership.org_id === activeOrg.id ? 'bg-muted' : ''
                        }
                      >
                        <div className="flex flex-col">
                          <span>{membership.organization.name}</span>
                          <span className="text-xs text-muted-foreground">
                            {membership.role}
                          </span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}

          <Button variant="outline" size="icon" onClick={handleSignOut}>
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </header>
  );
};

export default Topbar;
